/**
 * General job costs — the non-trade money every job carries: permits and
 * fees, equipment rental, site and general conditions, consumables, and
 * crew travel.
 *
 * These are the line items a trade estimate forgets and a job loses money on.
 * Ships in the bundle so it works offline. Prices are seed values (national
 * average 2025-26 era rental / fee pricing) and hours are the crew hours the
 * item costs you (set up a lift, load a dumpster, drive to the job) — both are
 * editable on the device and every total re-rolls from the edit.
 */

export type GeneralSection = "permits" | "equipment" | "site" | "consumables" | "crew";

export interface GeneralItem {
  /** stable catalog id, unique across all sections */
  id: string;
  section: GeneralSection;
  label: string;
  /** how it reads on a takeoff, or "—" */
  size: string;
  /** buying unit */
  unit: "ea" | "day" | "week" | "month" | "hr" | "mi" | "ls";
  /** seed price per unit, $ */
  price: number;
  /** crew hours per unit this item eats */
  hours: number;
  /** short note — what it covers */
  spec: string;
}

export const GENERAL_SECTIONS: Array<{ id: GeneralSection; label: string }> = [
  { id: "permits", label: "Permits, fees & inspections" },
  { id: "equipment", label: "Equipment & tool rental" },
  { id: "site", label: "Site & general conditions" },
  { id: "consumables", label: "Consumables & safety" },
  { id: "crew", label: "Travel, per diem & crew costs" },
];

export const generalUnitLabel = (u: GeneralItem["unit"]) =>
  u === "ls" ? "lump" : u === "mi" ? "mile" : u;

/* ------------------------------------------------------------------ */

const PERMITS: Omit<GeneralItem, "section">[] = [
  { id: "permit-mech", size: "—", label: "Mechanical permit", unit: "ea", price: 450, hours: 1.5, spec: "AHJ filing, plan review fee" },
  { id: "permit-plumb", size: "—", label: "Plumbing permit", unit: "ea", price: 350, hours: 1.5, spec: "AHJ filing, fixture-count fee" },
  { id: "permit-elec", size: "—", label: "Electrical permit", unit: "ea", price: 400, hours: 1.5, spec: "AHJ filing, circuit-count fee" },
  { id: "permit-bldg", size: "—", label: "Building / structural permit", unit: "ea", price: 750, hours: 2, spec: "AHJ filing, valuation based" },
  { id: "inspection", size: "—", label: "Inspection visit", unit: "ea", price: 0, hours: 2, spec: "Meet inspector, rough or final" },
  { id: "engineering", size: "—", label: "Engineering / stamped drawings", unit: "ls", price: 1500, hours: 0, spec: "PE review and seal" },
  { id: "asbuilt", size: "—", label: "As-builts & O&M closeout", unit: "ls", price: 0, hours: 12, spec: "Red-lines, manuals, closeout book" },
  { id: "bond", size: "—", label: "Payment & performance bond", unit: "ls", price: 0, hours: 0, spec: "Typically 1–3% of contract" },
];

const EQUIPMENT: Omit<GeneralItem, "section">[] = [
  { id: "scissor-19", size: "19 ft", label: "Scissor lift rental", unit: "week", price: 425, hours: 1, spec: "Electric, slab only · incl. delivery setup" },
  { id: "scissor-32", size: "32 ft", label: "Scissor lift rental", unit: "week", price: 685, hours: 1, spec: "Electric, rough terrain optional" },
  { id: "boom-45", size: "45 ft", label: "Boom lift rental", unit: "week", price: 1250, hours: 1.5, spec: "Articulating, 4WD" },
  { id: "forklift", size: "5,000 lb", label: "Telehandler / forklift", unit: "week", price: 1450, hours: 1.5, spec: "Rough terrain, operator not incl." },
  { id: "crane-pick", size: "—", label: "Crane & operator, pick", unit: "day", price: 2200, hours: 8, spec: "Hydraulic truck crane, rigging crew extra" },
  { id: "welder-rental", size: "—", label: "Engine-drive welder rental", unit: "week", price: 385, hours: 0, spec: "Diesel, leads included" },
  { id: "genset", size: "20 kW", label: "Generator rental", unit: "week", price: 520, hours: 0.5, spec: "Towable, fuel not included" },
  { id: "compressor", size: "185 cfm", label: "Air compressor rental", unit: "week", price: 350, hours: 0.5, spec: "Towable diesel" },
  { id: "scaffold", size: "—", label: "Scaffold, erected", unit: "week", price: 18, hours: 0.05, spec: "Per frame section per week" },
  { id: "core-drill", size: "—", label: "Core drill rental", unit: "day", price: 165, hours: 0, spec: "Wet core rig, bits extra" },
  { id: "trench-box", size: "—", label: "Trench box / shoring", unit: "week", price: 480, hours: 2, spec: "Includes set and pull" },
];

const SITE: Omit<GeneralItem, "section">[] = [
  { id: "dumpster-20", size: "20 yd", label: "Dumpster / roll-off", unit: "ea", price: 585, hours: 2, spec: "Haul & tip fee, 2 ton allowance" },
  { id: "dumpster-30", size: "30 yd", label: "Dumpster / roll-off", unit: "ea", price: 725, hours: 2.5, spec: "Haul & tip fee, 3 ton allowance" },
  { id: "jobtrailer", size: "8x20", label: "Job trailer / gang box", unit: "month", price: 275, hours: 3, spec: "Delivery, blocking and steps" },
  { id: "portajohn", size: "—", label: "Portable toilet", unit: "month", price: 165, hours: 0, spec: "Weekly service" },
  { id: "tempower", size: "—", label: "Temporary power & lighting", unit: "month", price: 400, hours: 4, spec: "Spider boxes, cords, string lights" },
  { id: "barricade", size: "—", label: "Barricade, signage & protection", unit: "ls", price: 250, hours: 3, spec: "Cones, tape, floor protection" },
  { id: "cleanup", size: "—", label: "Daily cleanup / final clean", unit: "hr", price: 0, hours: 1, spec: "Labor only, broom clean" },
  { id: "hoisting", size: "—", label: "Hoisting & material handling", unit: "hr", price: 0, hours: 1, spec: "Move material to the floor" },
  { id: "layout", size: "—", label: "Layout & coordination time", unit: "hr", price: 0, hours: 1, spec: "BIM coordination, field layout" },
  { id: "mobilize", size: "—", label: "Mobilize / demobilize", unit: "ls", price: 350, hours: 8, spec: "Load in, load out, tool haul" },
];

const CONSUMABLES: Omit<GeneralItem, "section">[] = [
  { id: "consum-weld", size: "—", label: "Welding gas & rod allowance", unit: "day", price: 85, hours: 0, spec: "Argon/CO₂, filler, cups, tungsten" },
  { id: "consum-blade", size: "—", label: "Blades, bits & wheels", unit: "week", price: 120, hours: 0, spec: "Sawzall, hole saws, grinding wheels" },
  { id: "consum-anchor", size: "—", label: "Misc. fasteners & anchors", unit: "ls", price: 200, hours: 0, spec: "Screws, wedge anchors, strut nuts" },
  { id: "consum-rag", size: "—", label: "Rags, solvent & marking", unit: "week", price: 45, hours: 0, spec: "Cleaner, markers, layout paint" },
  { id: "ppe", size: "—", label: "PPE per worker", unit: "ea", price: 165, hours: 0, spec: "Hard hat, glasses, gloves, vest, boots allowance" },
  { id: "fire-watch", size: "—", label: "Fire watch / hot work", unit: "hr", price: 0, hours: 1, spec: "Extinguisher, blankets, watch time" },
  { id: "fuel", size: "—", label: "Fuel & propane", unit: "week", price: 150, hours: 0, spec: "Equipment fuel, torch gas" },
  { id: "smalltools", size: "—", label: "Small tools allowance", unit: "ls", price: 0, hours: 0, spec: "Usually a % of labor — see Labor tab" },
];

const CREW: Omit<GeneralItem, "section">[] = [
  { id: "truck", size: "—", label: "Truck & fuel charge", unit: "day", price: 95, hours: 0, spec: "Service truck on site" },
  { id: "mileage", size: "—", label: "Mileage", unit: "mi", price: 0.7, hours: 0, spec: "IRS business rate ballpark" },
  { id: "perdiem", size: "—", label: "Per diem, per worker", unit: "day", price: 165, hours: 0, spec: "Lodging + meals, out of town" },
  { id: "travel-hr", size: "—", label: "Travel time, paid", unit: "hr", price: 0, hours: 1, spec: "Portal-to-portal per agreement" },
  { id: "parking", size: "—", label: "Parking & tolls", unit: "day", price: 35, hours: 0, spec: "Downtown / secured lots" },
  { id: "training", size: "—", label: "Site orientation & badging", unit: "ea", price: 0, hours: 2, spec: "Per worker, owner safety orientation" },
  { id: "drug-test", size: "—", label: "Drug test / background", unit: "ea", price: 65, hours: 1, spec: "Per worker, site requirement" },
];

export const GENERAL_ITEMS: GeneralItem[] = [
  ...PERMITS.map((i) => ({ ...i, section: "permits" as const })),
  ...EQUIPMENT.map((i) => ({ ...i, section: "equipment" as const })),
  ...SITE.map((i) => ({ ...i, section: "site" as const })),
  ...CONSUMABLES.map((i) => ({ ...i, section: "consumables" as const })),
  ...CREW.map((i) => ({ ...i, section: "crew" as const })),
];

const BY_ID = new Map(GENERAL_ITEMS.map((i) => [i.id, i]));

export const getGeneralItem = (id: string) => BY_ID.get(id);

export const generalItems = (section: GeneralSection) =>
  GENERAL_ITEMS.filter((i) => i.section === section);
